import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ExternalLink, X } from "lucide-react";
import { generateQuiz } from "~/api/v2/requests";
import { Button } from "~/atoms/button";
import { Textarea } from "~/atoms/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/atoms/dialog";

interface DocumentToAnchor {
  documentId: number;
  documentVersionId: number;
  title: string;
}

interface Item extends DocumentToAnchor {
  prompt: string;
}

interface Props {
  documents: DocumentToAnchor[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

const toItems = (documents: DocumentToAnchor[]): Item[] =>
  documents.map((document) => ({ ...document, prompt: "" }));

/**
 * Bulk quiz creation. Lists the selected documents, each with its own optional
 * prompt and remove button, and fires one generate request per quiz. Generation
 * runs in the background, so once the requests are in flight `onCreated` hands
 * off to the quizzes page where each quiz shows up and finishes on its own.
 *
 * Mounted only while open, so the document list seeds fresh state each time.
 */
const CreateQuizzesDialog = ({
  documents,
  open,
  onOpenChange,
  onCreated,
}: Props) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<Item[]>(() => toItems(documents));
  const [isCreating, setIsCreating] = useState(false);

  const setPrompt = (documentVersionId: number, prompt: string) =>
    setItems((prev) =>
      prev.map((item) =>
        item.documentVersionId === documentVersionId
          ? { ...item, prompt }
          : item,
      ),
    );

  const removeItem = (documentVersionId: number) =>
    setItems((prev) =>
      prev.filter((item) => item.documentVersionId !== documentVersionId),
    );

  const create = async () => {
    setIsCreating(true);
    const results = await Promise.all(
      items.map(async (item) => {
        try {
          await generateQuiz(
            item.documentVersionId,
            item.prompt.trim() || undefined,
          );
          return true;
        } catch {
          return false;
        }
      }),
    );
    setIsCreating(false);

    const failed = results.filter((ok) => !ok).length;
    if (failed > 0) {
      toast.error(t(($) => $.anchoring.create.error, { count: failed }));
    }

    // As long as something started, hand off to the quizzes page where the
    // generating quizzes appear and finish on their own.
    if (failed < results.length) {
      onCreated();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-200 flex-col">
        <DialogHeader>
          <DialogTitle>{t(($) => $.anchoring.create.title)}</DialogTitle>
          <DialogDescription>
            {t(($) => $.anchoring.create.description)}
          </DialogDescription>
        </DialogHeader>
        <ul className="flex flex-col gap-3 overflow-y-auto">
          {items.map((item) => (
            <li
              key={item.documentVersionId}
              className="flex items-start gap-3 border-b pb-3"
            >
              <div className="flex w-1/3 items-center gap-2">
                <span className="line-clamp-2">{item.title}</span>
                <a
                  href={`/documents/${item.documentId}?versionId=${item.documentVersionId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-muted-foreground hover:text-foreground shrink-0"
                >
                  <ExternalLink className="size-4" />
                </a>
              </div>
              <Textarea
                value={item.prompt}
                onChange={(event) =>
                  setPrompt(item.documentVersionId, event.target.value)
                }
                placeholder={t(($) => $.anchoring.create.promptPlaceholder)}
                disabled={isCreating}
                className="min-h-10 flex-1"
              />
              <div className="flex h-9 w-6 shrink-0 items-center justify-center">
                <button
                  type="button"
                  onClick={() => removeItem(item.documentVersionId)}
                  disabled={isCreating}
                  className="text-muted-foreground hover:text-destructive disabled:opacity-50"
                >
                  <X className="size-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button onClick={create} disabled={isCreating || items.length === 0}>
            {t(($) => $.anchoring.create.create)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateQuizzesDialog;
